import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';

import {
    EventModel,
    EventOption,
    EventOptionGroup,
    EventOptionGroupType,
    FootballScore,
    PairScore,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { MarketType } from '@frontend/sports/betting-offer/feature/types';
import { GridConfig as MsGrid, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { FootballPeriodInfo } from '@frontend/sports/common/core/data-access/constants';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { GridMedia, Group } from '@frontend/sports/grid/core/feature/model';
import { NOT_APPLICABLE, TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { clone, cloneDeep, keys } from 'lodash-es';

import { ScoreboardSlimSubscriptionService } from '../../../event-subscription/scoreboard-slim-subscription.service';
import { FavouriteEventToggleComponent } from '../../../favourites/toggle/favourite-event-toggle.component';
import { CompactEventService } from '../../../grid-base/compact-event.service';
import { GridScoreboardComponent } from '../../../grid-scoreboard/components/grid-scoreboard.component';
import { EventDetailComponent } from '../../../grid/content/event-detail.component';
import { EventMediaComponent } from '../../../grid/content/event-media.component';
import { FixtureFormComponent } from '../../../grid/content/form-indicator/fixture-form.component';
import { OptionGroupComponent } from '../../../grid/content/option-group/option-group.component';
import { ScoreDetails } from '../../../highlights-marquee/marquee-tile.model';
import { RedirectHelperService } from '../../../navigation-core/redirect-helper.service';
import { RemoveHrefDirective } from '../../../navigation-core/remove-href.directive';
import { EventLinkConfig, UrlHelperService } from '../../../navigation-core/url-helper.service';
import { OddIncrementDecrementComponent } from '../../../odd-increment-decrement/odd-increment-decrement.component';

@Component({
    selector: 'ms-correct-score-option-group',
    templateUrl: 'correct-score-option-group.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgFor,
        NgIf,
        NgClass,
        NgTemplateOutlet,
        FontResizerComponent,
        FavouriteEventToggleComponent,
        RemoveHrefDirective,
        GridScoreboardComponent,
        EventMediaComponent,
        OddIncrementDecrementComponent,
        EventDetailComponent,
        FixtureFormComponent,
        OptionGroupComponent,
    ],
})
export class CorrectScoreOptionGroupComponent implements OnInit, OnChanges, OnDestroy {
    @Input() couponEvent: EventModel;
    @Input() couponGroups: Group[];
    @Input() tracking: TrackingSource;
    @Input() eventLinkConfig: EventLinkConfig;
    @Input() media: GridMedia;
    @Input() promotedFixtureIdsMap: { [fixtureId: number]: number } = {};
    @Input() showParticipantImage: boolean;
    @Input() betDetails = '';
    @Input() favouriteToggle = false;

    @Output() moreBetsClicked = new EventEmitter<void>();
    @Output() statsClicked = new EventEmitter<void>();

    @HostBinding('class') className = 'correct-score-coupon';

    @HostBinding('class.one-groupoptions')
    get threeColumn(): boolean {
        return !this.couponGroups || this.couponGroups.length === 1;
    }

    showOptionName: boolean;
    betBuilderEpcotEnabled = false;
    groupTemplate: TemplateRef<any>;
    scoreBoardEvent: EventModel;
    eventUrl: string;
    shouldRemoveHref: Nullable<boolean>;
    empty: boolean;
    emptyText: string;
    emptyChars = 15;
    groupsMap: { [key: string]: EventOptionGroup };
    hasBetsCountButton: boolean;
    betsCountMessage: string;
    scoreBoardMap: { [key: string]: { participantScore: number; participantLiveScore: number } } = {};

    private pushSubscription: EventSubscription<EventModel>;
    participant1Position = 0;
    participant2Position = 1;
    showTimeInfoOnTop = false;

    constructor(
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private deviceService: DeviceService,
        private nativeAppService: NativeAppService,
        private redirectHelper: RedirectHelperService,
        private compactEventService: CompactEventService,
        private sbSlimSubscriptionService: ScoreboardSlimSubscriptionService,
        private trackingService: TrackingService,
        private msGrid: MsGrid,
    ) {}

    ngOnInit(): void {
        this.setOptionGroup();
        this.setLink();
        this.hasBetsCountButton = this.gridConfig?.hasBetsCountButton ?? false;
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
        this.subscribe();
        this.showTimeInfoOnTop = this.msGrid.gridEventDisplayVersion === 'V2';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.couponGroups || changes.couponEvent) {
            if (changes.couponEvent && changes.couponEvent.isFirstChange()) {
                this.scoreBoardEvent = cloneDeep(this.couponEvent);
            }

            this.setScoreBoard();
            this.setOptionGroup();
            this.setLink();
            this.betsCountMessage = interpolateDoubleBracesString(this.sitecore.eventGrid.BetsLinkText, {
                count: this.couponEvent?.totalMarketsCount,
            });
        }

        if (changes.eventLinkConfig) {
            this.setLink();
        }
    }

    trackOptionGroup(index: number, optionGroup: EventOptionGroup): string {
        return optionGroup.id;
    }

    eventClick(event: Event): void {
        if (!this.deviceService.isTouch && !this.nativeAppService.isTerminal) {
            if (hasTarget(event, HTMLAnchorElement)) {
                return;
            }

            this.redirectHelper.goToPage(this.eventUrl);
        }
    }

    private setLink(): void {
        this.eventUrl = this.urlHelper.getEventUrl(this.couponEvent, this.eventLinkConfig.extraParams);
        this.emptyText = this.eventLinkConfig.emptyText;
    }

    private setOptionGroup(): void {
        this.groupsMap = {};

        if (!this.couponGroups) {
            this.empty = true;
        } else {
            this.couponGroups.forEach((group) => {
                const participant1Score = this.scoreBoardMap[`${group.id}-${this.participant1Position}`]?.participantScore || 0;
                const participant2Score = this.scoreBoardMap[`${group.id}-${this.participant2Position}`]?.participantScore || 0;
                const optionGroup = this.getCurrentOptionGroup(participant1Score, participant2Score, group.id);

                if (optionGroup) {
                    this.groupsMap[group.id] = optionGroup;
                }
            });

            const optionGroups = keys(this.groupsMap || {});
            this.empty = optionGroups.length === 0 || optionGroups.every((x) => !this.groupsMap[x]?.online);
        }
    }

    private setScoreBoard(): void {
        const groupKeys = keys(this.scoreBoardMap || {});
        this.couponGroups.forEach((group) => {
            if (!groupKeys.includes(`${group.id}-${this.participant1Position}`)) {
                this.setInitialScore(group.id);
            }
        });

        this.updateInitialLiveScores();
    }

    private setInitialScore(groudId: string): void {
        this.scoreBoardMap[`${groudId}-${this.participant1Position}`] = {
            participantScore: 0,
            participantLiveScore: 0,
        };
        this.scoreBoardMap[`${groudId}-${this.participant2Position}`] = {
            participantScore: 0,
            participantLiveScore: 0,
        };
    }

    private updateInitialLiveScores(): void {
        if (!this.scoreBoardEvent || !this.scoreBoardEvent.live || this.scoreBoardEvent.scoreboard.period?.id === 0) {
            return;
        }

        const score = this.scoreBoardEvent.scoreboard.getScore<FootballScore>();
        if (!score) {
            return;
        }

        this.updateScores(score);

        const firstHalfScore: PairScore<number> = {
            participant1: Number(score.firstHalfScore?.participant1) >= 0 ? Number(score.firstHalfScore?.participant1) : score.participant1,
            participant2: Number(score.firstHalfScore?.participant2) >= 0 ? Number(score.firstHalfScore?.participant2) : score.participant2,
        };
        this.updateScores(firstHalfScore, FootballPeriodInfo.FirstHalf);

        if (score.firstHalfScore) {
            const secondHalfScore: PairScore<number> = {
                participant1: Number(score.participant1 || 0) - Number(score.firstHalfScore.participant1),
                participant2: Number(score.participant2 || 0) - Number(score.firstHalfScore.participant2),
            };
            this.updateScores(secondHalfScore, FootballPeriodInfo.SecondHalf);
        }
    }

    protected updateScoreboard = (updatedEvent: EventModel): void => {
        if (updatedEvent) {
            this.scoreBoardEvent = cloneDeep(updatedEvent);
            this.scoreBoardEvent.live = this.scoreBoardEvent.scoreboard.started;
        }
        const scoreBoardPeriodId = this.scoreBoardEvent.scoreboard.period?.id;

        if (!scoreBoardPeriodId) {
            return;
        }
        const score = this.scoreBoardEvent.scoreboard.getScore<FootballScore>();
        const participant1 = <ScoreDetails>{ position: 0, score: score?.participant1 };
        const participant2 = <ScoreDetails>{ position: 1, score: score?.participant2 };

        const regularGroupId = this.getGroupPeriodId();
        if (regularGroupId) {
            this.updateLiveScore(participant1, participant2, regularGroupId);
        }

        if (this.scoreBoardEvent.scoreboard.period?.id === FootballPeriodInfo.SecondHalf) {
            const firstHalfScore = this.firstHalfScore();
            participant1.score = participant1.score - Number(firstHalfScore?.participant1);
            participant2.score = participant2.score - Number(firstHalfScore?.participant2);
        }
        const groupId = this.getGroupPeriodId(scoreBoardPeriodId);
        if (groupId) {
            this.updateLiveScore(participant1, participant2, groupId);
        }
    };

    updateScores(score: PairScore<number>, footballPeriodId?: number) {
        const periodId = this.getGroupPeriodId(footballPeriodId);
        if (periodId) {
            this.scoreBoardMap[`${periodId}-${this.participant1Position}`] = {
                participantScore: this.scoreBoardMap[`${periodId}-${this.participant1Position}`]?.participantScore || score.participant1 || 0,
                participantLiveScore: score.participant1 || 0,
            };
            this.scoreBoardMap[`${periodId}-${this.participant2Position}`] = {
                participantScore: this.scoreBoardMap[`${periodId}-${this.participant2Position}`]?.participantScore || score.participant2 || 0,
                participantLiveScore: score.participant2 || 0,
            };
        }
    }

    private updateLiveScore(participant1: ScoreDetails, participant2: ScoreDetails, groupId: string | undefined): void {
        if (participant1 && participant2 && groupId) {
            this.scoreBoardMap[`${groupId}-${participant1.position}`].participantLiveScore = participant1.score;
            this.scoreBoardMap[`${groupId}-${participant2.position}`].participantLiveScore = participant2.score;
        }
    }

    private firstHalfScore(): PairScore<number> | undefined {
        if (this.couponEvent.scoreboard.period && this.couponEvent.scoreboard.period.id === FootballPeriodInfo.SecondHalf) {
            const score = this.couponEvent.scoreboard.getScore<FootballScore>()!;

            return score?.firstHalfScore;
        }

        return;
    }

    private getGroupPeriodId(periodId?: number): string | undefined {
        if (periodId) {
            return this.couponGroups.find((group) => group.scoreBoardPeriodId === periodId)?.id;
        }

        return this.couponGroups.find((group) => !group.scoreBoardPeriodId)?.id;
    }

    trackMoreBets(event: Event): void {
        this.moreBetsClicked.emit();
        event.stopPropagation();
    }

    changeScoreEvent(scoreDetails: ScoreDetails, groupId: string): void {
        if (!Object.keys(this.scoreBoardMap).includes(`${groupId}-${scoreDetails.position}`)) {
            return;
        }
        const eventDetails = this.scoreBoardMap[`${groupId}-${scoreDetails.position}`].participantScore > scoreDetails.score ? '-' : '+';
        this.scoreBoardMap[`${groupId}-${scoreDetails.position}`].participantScore = scoreDetails.score;
        const p1Score = this.scoreBoardMap[`${groupId}-${this.participant1Position}`];
        const p2Score = this.scoreBoardMap[`${groupId}-${this.participant2Position}`];
        this.groupsMap[groupId] = this.getCurrentOptionGroup(p1Score.participantScore, p2Score.participantScore, groupId);
        this.sendTracking(eventDetails);
    }

    private getCurrentOptionGroup(p1Score: number, p2Score: number, activePeriod: string): EventOptionGroup {
        const market = `${p1Score}:${p2Score}`;
        const marketDash = `${p1Score}-${p2Score}`;
        const eventOptionGroups = this.compactEventService.prepareEventOptionGroups(this.couponEvent.optionGroups);
        const optionGroup = eventOptionGroups.find(
            (og) => og.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.CorrectScore,
        );
        const options = this.getOptionsActive(Number(activePeriod), optionGroup)?.options;

        let filteredOption = options?.find((option) => option.name === market || option.name === marketDash);
        if (!filteredOption) {
            filteredOption = options?.find((option) => !option.name.includes(':') && !option.name.includes('-')) ?? new EventOption();
        }

        const filteredOptionGroup = Object.assign(new EventOptionGroup(), optionGroup);
        if (filteredOption) {
            filteredOptionGroup.options = [filteredOption];
        }

        return filteredOptionGroup;
    }

    private getOptionsActive(active: number, optionGroup?: EventOptionGroup): EventOptionGroup | undefined {
        if (optionGroup?.hasSubGroups(EventOptionGroupType.Period)) {
            return optionGroup.getSubGroups(EventOptionGroupType.Period).find((p) => p.detailedGrouping && p.detailedGrouping.periodGroup === active);
        }

        return optionGroup?.detailedGrouping.periodGroup === active ? optionGroup : undefined;
    }

    private subscribe(): void {
        if (this.couponEvent) {
            this.pushSubscription = this.sbSlimSubscriptionService.subscribe([{ event: clone(this.couponEvent), callback: this.updateScoreboard }]);
        }
    }

    ngOnDestroy(): void {
        this.pushSubscription?.unsubscribe();
    }

    private sendTracking(eventDetails: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports coupons',
            [trackingConstants.COMPONENT_LABEL_EVENT]: this.betDetails,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.couponEvent.name,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }
}
